import React, { useState, useEffect, useCallback } from "react";
import { TrendingUp, Users, Target, BarChart3, Bell, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  critique: "bg-red-100 text-red-700",
  fragile: "bg-orange-100 text-orange-700",
  "améliorable": "bg-yellow-100 text-yellow-700",
  solide: "bg-green-100 text-green-700",
};

export const KPIDashboard = () => {
  const [kpi, setKpi] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [kRes, nRes] = await Promise.all([
        axios.get(`${API}/admin/config/kpi`),
        axios.get(`${API}/admin/config/notifications`),
      ]);
      setKpi(kRes.data);
      setNotifs(nRes.data);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/config/notifications/read-all`);
      load();
    } catch { /* */ }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> KPI Diagnostics</h2>
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!kpi) return null;

  const maxWeekly = Math.max(...kpi.weekly_trend.map((w) => w.count), 1);

  return (
    <div className="space-y-4">
      {/* Notifications */}
      {notifs && notifs.unread > 0 && (
        <div className="bg-accent/5 border-2 border-accent/30 rounded-lg p-4" data-testid="notifications-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Bell className="w-4 h-4 text-accent" />
              {notifs.unread} nouvelle{notifs.unread > 1 ? "s" : ""} notification{notifs.unread > 1 ? "s" : ""}
            </h3>
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs">Tout marquer lu</Button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {notifs.notifications.filter((n) => !n.read).slice(0, 5).map((n, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-white rounded px-3 py-2 border">
                <CheckCircle2 className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                <span className="truncate">{n.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="bg-card rounded-lg border p-6" data-testid="kpi-dashboard">
        <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" /> KPI Diagnostics
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total diagnostics" value={kpi.total_diagnostics} color="text-primary" />
          <StatCard icon={CheckCircle2} label="Avec contact" value={kpi.contacted} color="text-green-600" />
          <StatCard icon={Target} label="Taux complétion" value={`${kpi.completion_rate}%`} color="text-accent" />
          <StatCard icon={BarChart3} label="Score moyen" value={`${kpi.avg_score}/100`} color="text-primary" />
        </div>

        {/* Weekly trend */}
        <div className="mb-6">
          <p className="text-sm font-semibold mb-3">Tendance hebdomadaire</p>
          <div className="flex items-end gap-1 h-24">
            {kpi.weekly_trend.map((w, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] font-bold text-slate-700">{w.count || ""}</span>
                <div
                  className="w-full rounded-t bg-primary/80 transition-all"
                  style={{ height: `${Math.max((w.count / maxWeekly) * 100, w.count > 0 ? 10 : 2)}%`, minHeight: "2px" }}
                />
                <span className="text-[9px] text-muted-foreground">{w.week}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By level + By metier */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold mb-2">Par niveau</p>
            <div className="space-y-1.5">
              {["critique", "fragile", "améliorable", "solide"].map((lvl) => (
                <div key={lvl} className="flex items-center justify-between text-xs bg-muted/30 rounded px-3 py-1.5">
                  <span className={`px-2 py-0.5 rounded font-medium ${LEVEL_COLORS[lvl]}`}>{lvl}</span>
                  <span className="font-bold">{kpi.by_level?.[lvl] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Par métier</p>
            <div className="space-y-1.5">
              {(kpi.by_metier || []).slice(0, 6).map((m) => (
                <div key={m.metier} className="flex items-center justify-between text-xs bg-muted/30 rounded px-3 py-1.5">
                  <span className="text-muted-foreground">{m.metier}</span>
                  <span className="font-bold">{m.count}</span>
                </div>
              ))}
              {(!kpi.by_metier || kpi.by_metier.length === 0) && (
                <p className="text-xs text-muted-foreground">Aucune donnée</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-muted/50 rounded-lg p-4 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />
      <p className="text-2xl font-black">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
